"use client";

import * as React from "react";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { cn } from "../../lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "../Carousel";
import { PACKAGES_GRID_STYLES } from "./constants";
import { PackageCard } from "./PackageCard";
import type { PackagesGridProps } from "./types";

export const PackagesGrid = React.forwardRef<HTMLElement, PackagesGridProps>(
  ({ className, title, description, cards, onCardClick, ...props }, ref) => {
    const [api, setApi] = React.useState<CarouselApi>();
    const [showArrows, setShowArrows] = React.useState(false);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      const updateArrows = () => {
        setShowArrows(api.canScrollPrev() || api.canScrollNext());
      };

      updateArrows();
      api.on("select", updateArrows);
      api.on("reInit", updateArrows);
      api.on("resize", updateArrows);

      return () => {
        api.off("select", updateArrows);
        api.off("reInit", updateArrows);
        api.off("resize", updateArrows);
      };
    }, [api]);

    return (
      <section
        ref={ref}
        className={cn(PACKAGES_GRID_STYLES.root, className)}
        {...props}
      >
        <div className={PACKAGES_GRID_STYLES.header}>
          <div>
            <h2 className={PACKAGES_GRID_STYLES.title}>{title}</h2>
            <p className={PACKAGES_GRID_STYLES.description}>{description}</p>
          </div>
        </div>

        <div className={PACKAGES_GRID_STYLES.gridWrapper}>
          {showArrows && (
            <button
              type="button"
              onClick={() => api?.scrollPrev()}
              className={cn(PACKAGES_GRID_STYLES.sideNavButton, PACKAGES_GRID_STYLES.sideNavLeft)}
              aria-label="Anterior"
            >
              <LuArrowLeft className="h-4 w-4" />
            </button>
          )}

          <Carousel
            className={PACKAGES_GRID_STYLES.carousel}
            opts={{ align: "start", loop: true }}
            setApi={setApi}
          >
            <CarouselContent
              className={PACKAGES_GRID_STYLES.carouselContent}
              viewportClassName={PACKAGES_GRID_STYLES.carouselViewport}
            >
              {cards.map((card) => (
                <CarouselItem
                  key={card.id}
                  className={PACKAGES_GRID_STYLES.carouselItem}
                >
                  <PackageCard
                    {...card}
                    onClick={(event) => {
                      if (card.analyticsData && card.analyticsUrl) {
                        try {
                          const payload = btoa(unescape(encodeURIComponent(JSON.stringify(card.analyticsData))));
                          navigator.sendBeacon(card.analyticsUrl, payload);
                        } catch {
                          // Ignore analytics failures to avoid impacting UX
                        }
                      }
                      onCardClick?.(card, event);
                    }}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {showArrows && (
            <button
              type="button"
              onClick={() => api?.scrollNext()}
              className={cn(PACKAGES_GRID_STYLES.sideNavButton, PACKAGES_GRID_STYLES.sideNavRight)}
              aria-label="Siguiente"
            >
              <LuArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </section>
    );
  }
);

PackagesGrid.displayName = "PackagesGrid";
